/**
 * Payment instructions, opened once (inbox 041, completing FS-056).
 *
 * The backend half of FS-056 shipped without this page, so a link a dealer sent
 * went nowhere. The person reading it is **not a user of this product** — they
 * are a buyer about to wire six figures — which shapes every decision here.
 *
 * Three things this page does that are not obvious:
 *
 * **It takes the token out of the address bar immediately.** The token travels
 * in the URL fragment so it never reaches a server log, a `Referer` header or
 * an analytics query string. That protects it in transit and does nothing about
 * the browser: a fragment sits in the address bar, in history, and in whatever
 * the recipient pastes into an email asking "is this real?". So the fragment is
 * read into memory once and `history.replaceState` wipes it on mount.
 *
 * **It warns before opening, not after.** The link opens once. A page that
 * explains that *after* consuming it has told the recipient something they can
 * no longer act on, and the dealer gets a phone call. The notice is above the
 * button.
 *
 * **It keeps the numbers in memory and nowhere else.** No `localStorage`, no
 * URL, no query cache that survives a reload — a reload after redemption gets
 * the honest answer, which is that the link is spent. Copy and print are
 * offered because saving them is the recipient's job and the page should make
 * it one action rather than six selections.
 */

import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Building2,
  Check,
  Copy,
  Lock,
  Printer,
  ShieldAlert,
} from "lucide-react";
import { apiClient } from "@/api/client";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import Input from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface Peek {
  openable: boolean;
  reason: string | null;
  email_hint: string | null;
  purpose: string | null;
  expires_at: string | null;
}

interface Instructions {
  label: string;
  bank_name: string;
  account_holder_name: string;
  account_type: string;
  account_number: string;
  routing_number: string;
  swift_code: string | null;
  iban: string | null;
  bank_address: string | null;
  wire_instructions: string | null;
  wire_limit: string | null;
  purpose: string | null;
  message: string | null;
  sender_organization: string | null;
  opened_at: string;
  notice: string;
}

/** What each refusal means, in the recipient's words rather than the system's. */
interface Refusal {
  title: string;
  body: string;
}

const NOT_FOUND: Refusal = {
  title: "This link is not valid",
  body: "Check that you opened the most recent email, and that the whole link was copied. If you were not expecting payment instructions, contact the sender on a phone number you already have — not one from an email.",
};

const REFUSALS: Record<string, Refusal> = {
  not_found: {
    title: "This link is not valid",
    body: "Check that you opened the most recent email, and that the whole link was copied. If you were not expecting payment instructions, contact the sender on a phone number you already have — not one from an email.",
  },
  expired: {
    title: "This link has expired",
    body: "Links stop working after a set time so they cannot be used later by somebody else. Ask the sender for a new one.",
  },
  redeemed: {
    title: "This link has already been opened",
    body: "Payment instructions open once. If you did not open it, tell the sender straight away — they can see when and from where it was opened.",
  },
  cancelled: {
    title: "This link was cancelled",
    body: "The sender withdrew it. Contact them for current instructions.",
  },
  locked: {
    title: "This link is locked",
    body: "Too many wrong email addresses were entered, so it stopped accepting any. Ask the sender for a new one.",
  },
};

function BankingRedeemPage() {
  // The token, read from the fragment once and never put back.
  const token = useRef<string>("");
  const [loading, setLoading] = useState(true);
  const [peek, setPeek] = useState<Peek | null>(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<Instructions | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fragment = window.location.hash.replace(/^#/, "").trim();
    token.current = fragment;

    // Out of the address bar, history and anything the recipient pastes.
    if (fragment) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }

    if (!fragment) {
      setPeek({
        openable: false,
        reason: "not_found",
        email_hint: null,
        purpose: null,
        expires_at: null,
      });
      setLoading(false);
      return;
    }

    apiClient
      .post<Peek>("/api/v1/public/banking/peek", { token: fragment })
      .then((resp) => setPeek(resp.data))
      .catch(() =>
        setPeek({
          openable: false,
          reason: "not_found",
          email_hint: null,
          purpose: null,
          expires_at: null,
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  const open = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const resp = await apiClient.post<Instructions>(
        "/api/v1/public/banking/redeem",
        { token: token.current, email: email.trim() },
      );
      setInstructions(resp.data);
    } catch (err: unknown) {
      // The server's own sentence. It is deliberately the same for a wrong
      // address and a spent link — telling an interceptor which of the two they
      // got is telling them whether to keep guessing.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "That did not work. Check the address and try again.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const asText = (i: Instructions) =>
    [
      i.sender_organization
        ? `Payment instructions from ${i.sender_organization}`
        : "Payment instructions",
      i.purpose ? `For: ${i.purpose}` : null,
      "",
      `Account name:   ${i.account_holder_name}`,
      `Bank:           ${i.bank_name}`,
      `Account type:   ${i.account_type}`,
      `Account number: ${i.account_number}`,
      `Routing number: ${i.routing_number}`,
      i.swift_code ? `SWIFT/BIC:      ${i.swift_code}` : null,
      i.iban ? `IBAN:           ${i.iban}` : null,
      i.bank_address ? `Bank address:   ${i.bank_address}` : null,
      i.wire_limit ? `Stated limit:   ${i.wire_limit}` : null,
      "",
      i.wire_instructions ?? null,
      "",
      `Opened ${new Date(i.opened_at).toLocaleString()}`,
    ]
      .filter((line) => line !== null)
      .join("\n");

  const copy = async (i: Instructions) => {
    try {
      await navigator.clipboard.writeText(asText(i));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard access is refused in plenty of ordinary situations — an
      // insecure origin, a locked-down browser. Saying so beats a button that
      // looks like it worked.
      setError(
        "Your browser would not let the page copy. Select the details and copy them, or use Print.",
      );
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--surface-sunken)]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-sunken)] px-4 py-10">
      <div className="mx-auto max-w-xl space-y-4">
        <div className="flex items-center gap-2 text-[var(--text-secondary)]">
          <Lock className="h-4 w-4" />
          <span className="text-sm font-medium">
            Secure payment instructions
          </span>
        </div>

        {instructions ? (
          <Instructed
            instructions={instructions}
            onCopy={copy}
            copied={copied}
            error={error}
          />
        ) : peek?.openable ? (
          <Card>
            <div className="space-y-4">
              <div>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">
                  Confirm it is you
                </h1>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  Enter the email address these instructions were sent to.
                  {peek.email_hint ? (
                    <>
                      {" "}
                      It looks like{" "}
                      <span className="font-mono font-semibold text-[var(--text-primary)]">
                        {peek.email_hint}
                      </span>
                      .
                    </>
                  ) : null}
                </p>
              </div>

              {peek.purpose && (
                <p className="rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-3 py-2 text-sm text-[var(--text-secondary)]">
                  For <span className="font-semibold">{peek.purpose}</span>
                </p>
              )}

              {/* Before the button, not after. */}
              <div className="flex gap-2.5 rounded-[var(--radius-md)] border border-[var(--text-danger)]/30 bg-[var(--fill-danger)] px-3 py-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-danger)]" />
                <p className="text-xs text-[var(--text-secondary)]">
                  <span className="font-semibold text-[var(--text-primary)]">
                    This opens once.
                  </span>{" "}
                  Have somewhere ready to save the details before you continue —
                  the link will not open a second time.
                  {peek.expires_at ? (
                    <>
                      {" "}
                      It stops working on{" "}
                      {new Date(peek.expires_at).toLocaleString()}.
                    </>
                  ) : null}
                </p>
              </div>

              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (email.trim()) open();
                }}
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                  aria-label="The email address this was sent to"
                />
                {error && (
                  <p
                    className="text-sm text-[var(--text-danger)]"
                    role="alert"
                    aria-live="polite"
                  >
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  isLoading={submitting}
                  disabled={!email.trim()}
                >
                  Open payment instructions
                </Button>
              </form>

              <p className="flex gap-2 text-xs text-[var(--text-tertiary)]">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                If you were not expecting this, do not continue. Contact the
                sender on a phone number you already have — not one from an
                email.
              </p>
            </div>
          </Card>
        ) : (
          <Refused reason={peek?.reason ?? "not_found"} />
        )}
      </div>
    </div>
  );
}

function Refused({ reason }: { reason: string }) {
  const copy = REFUSALS[reason] ?? NOT_FOUND;
  return (
    <Card>
      <div className="flex gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-danger)]" />
        <div>
          <h1 className="text-lg font-bold text-[var(--text-primary)]">
            {copy.title}
          </h1>
          <p className="mt-1.5 text-sm text-[var(--text-secondary)]">
            {copy.body}
          </p>
        </div>
      </div>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-[var(--border-subtle)] py-2 last:border-0 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="w-40 shrink-0 text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">
        {label}
      </span>
      <span className="select-all font-mono text-sm font-semibold text-[var(--text-primary)]">
        {value}
      </span>
    </div>
  );
}

function Instructed({
  instructions: i,
  onCopy,
  copied,
  error,
}: {
  instructions: Instructions;
  onCopy: (i: Instructions) => void;
  copied: boolean;
  error: string | null;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-secondary)]" />
            <div>
              <h1 className="text-xl font-bold text-[var(--text-primary)]">
                {i.sender_organization
                  ? `Payment instructions from ${i.sender_organization}`
                  : "Payment instructions"}
              </h1>
              {i.purpose && (
                <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                  For {i.purpose}
                </p>
              )}
            </div>
          </div>

          {/* The server's sentence, not ours. It is the one the dealer's own
              audit trail records having shown. */}
          <div className="flex gap-2.5 rounded-[var(--radius-md)] border border-[var(--text-danger)]/30 bg-[var(--fill-danger)] px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-danger)]" />
            <p className="text-xs font-medium text-[var(--text-primary)]">
              {i.notice}
            </p>
          </div>

          {i.message && (
            <p className="rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-3 py-2 text-sm text-[var(--text-secondary)]">
              {i.message}
            </p>
          )}

          <div>
            <Field label="Account name" value={i.account_holder_name} />
            <Field label="Bank" value={i.bank_name} />
            <Field label="Account type" value={i.account_type} />
            <Field label="Account number" value={i.account_number} />
            <Field label="Routing number" value={i.routing_number} />
            {i.swift_code && <Field label="SWIFT / BIC" value={i.swift_code} />}
            {i.iban && <Field label="IBAN" value={i.iban} />}
            {i.bank_address && (
              <Field label="Bank address" value={i.bank_address} />
            )}
            {i.wire_limit && (
              <Field label="Stated limit" value={i.wire_limit} />
            )}
          </div>

          {i.wire_instructions && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">
                From the sender
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--text-secondary)]">
                {i.wire_instructions}
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm text-[var(--text-danger)]" role="alert">
              {error}
            </p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button className="flex-1" onClick={() => onCopy(i)}>
              {copied ? (
                <>
                  <Check className="h-4 w-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" /> Copy all details
                </>
              )}
            </Button>
            <Button
              className="flex-1"
              variant="secondary"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4" /> Print or save as PDF
            </Button>
          </div>

          <p className="text-xs text-[var(--text-tertiary)]">
            Opened {new Date(i.opened_at).toLocaleString()}. Verify these
            details by phone with the sender, on a number you already have,
            before sending money.
          </p>
        </div>
      </Card>
    </div>
  );
}

export default BankingRedeemPage;
